/**
 * Web Push subscription + in-app new-mail chime helpers (iter-107).
 *
 * Browser-side workflow:
 *   1. Register the service worker (/sw.js).
 *   2. Ask user for Notification permission.
 *   3. PushManager.subscribe(...) using the backend's VAPID public key.
 *   4. POST the subscription to /api/email/push/subscribe.
 *
 * The service worker forwards "push:new_mail" messages to the page via
 * postMessage so foreground users hear the chime + see a toast even
 * if their browser dedupes the OS-level notification.
 */
import { api } from "./api/_client";

export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function getNotificationPermission() {
    if (typeof Notification === "undefined") return "default";
    return Notification.permission;
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/** Idempotent — register /sw.js, prompt for permission, subscribe to
 *  PushManager, send the subscription to the backend. Returns the
 *  subscription on success. */
export async function enablePushNotifications() {
    if (!isPushSupported()) throw new Error("This browser does not support Web Push notifications.");

    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;

    let perm = Notification.permission;
    if (perm === "default") {
        perm = await Notification.requestPermission();
    }
    if (perm !== "granted") throw new Error("Notification permission was denied.");

    // Fetch VAPID key from backend.
    const { data: keyResp } = await api.get("/email/push/vapid-public-key");
    const vapidKey = keyResp?.public_key;
    if (!vapidKey) throw new Error("Backend did not return a VAPID public key.");

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
    }

    const json = sub.toJSON();
    await api.post("/email/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys || {},
        user_agent: navigator.userAgent || "",
    });
    localStorage.setItem("planflow.push_enabled", "1");
    return sub;
}

export async function disablePushNotifications() {
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        try {
            await api.request({
                method: "DELETE",
                url: "/email/push/unsubscribe",
                data: { endpoint: sub.endpoint },
            });
        } catch { /* ignore */ }
        await sub.unsubscribe();
    }
    localStorage.removeItem("planflow.push_enabled");
}

export async function sendTestPush() {
    const { data } = await api.post("/email/push/test", {});
    return data;
}

/** Play a short two-tone chime via Web Audio API.
 *  Generated on-the-fly so we don't need to ship an audio asset. */
let _audioCtx = null;
export function playNewMailChime() {
    try {
        if (!_audioCtx) {
            _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = _audioCtx;
        if (ctx.state === "suspended") ctx.resume();
        const now = ctx.currentTime;

        const playTone = (freq, start, length) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, now + start);
            gain.gain.setValueAtTime(0, now + start);
            gain.gain.linearRampToValueAtTime(0.18, now + start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + start + length);
            osc.connect(gain).connect(ctx.destination);
            osc.start(now + start);
            osc.stop(now + start + length + 0.05);
        };
        // E5 → A5 → C#6 — a happy 3-note rising arpeggio.
        playTone(659.25, 0.00, 0.18);
        playTone(880.00, 0.14, 0.18);
        playTone(1108.73, 0.28, 0.22);
    } catch (e) {
        // Best-effort; some browsers block AudioContext until first
        // user gesture. The push notification itself will still fire.
    }
}

/** Attach a listener that fires the in-app chime + invokes ``onMail``
 *  whenever the service worker forwards a push:new_mail message. */
export function onPushNewMail(handler) {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return () => {};
    const wrapped = (event) => {
        if (event.data && event.data.kind === "push:new_mail") {
            playNewMailChime();
            try { handler(event.data.payload); } catch { /* ignore */ }
        }
    };
    navigator.serviceWorker.addEventListener("message", wrapped);
    return () => navigator.serviceWorker.removeEventListener("message", wrapped);
}
